import { useLocation, NavLink } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Shield,
  Home,
  Vault,
  Key,
  ShieldCheck,
  FileWarning,
  Trash2,
  Settings,
  LogOut,
  Users,
  Download,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import { ROUTES } from '@/constants/routes';
import { vaultShareService } from '@/services/vault-share-service';

export function MobileSidebar() {
  const { t } = useTranslation();

  const navItems = [
    { icon: Home, label: t('nav.dashboard'), to: ROUTES.DASHBOARD },
    { icon: Vault, label: t('nav.vaults'), to: ROUTES.VAULT },
    { icon: Key, label: t('nav.generator'), to: ROUTES.GENERATOR },
    { icon: ShieldCheck, label: t('nav.authenticator'), to: ROUTES.AUTHENTICATOR },
    { icon: FileWarning, label: t('nav.security'), to: ROUTES.SECURITY_REPORT },
    { icon: Trash2, label: t('nav.trash'), to: ROUTES.TRASH },
    { icon: Download, label: t('nav.downloads'), to: ROUTES.DOWNLOADS },
    { icon: Settings, label: t('nav.settings'), to: ROUTES.SETTINGS },
  ];
  const { logout, lock } = useAuthStore();
  const { sidebarOpen, setSidebarOpen } = useUIStore();
  const location = useLocation();

  const { data: sharedData } = useQuery({
    queryKey: ['shared-with-me'],
    queryFn: () => vaultShareService.listSharedWithMe(),
    staleTime: 30000,
  });

  const pendingCount = sharedData?.shares?.filter(s => s.status === 'PENDING').length || 0;
  const activeCount = sharedData?.shares?.filter(s => s.status === 'ACCEPTED').length || 0;

  // Close sidebar when route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, setSidebarOpen]);

  return (
    <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
      <SheetContent side="left" className="w-[280px] border-r-0 bg-sidebar p-0 text-sidebar-foreground [&>button]:text-white">
        <SheetHeader className="border-b border-white/10 p-5">
          <SheetTitle className="flex items-center gap-3 text-white">
            <img
              src="/logotipo.png"
              alt="Oneck Pass"
              className="h-9 w-9 object-contain"
            />
            <span className="text-lg font-semibold text-white">OneckPass</span>
          </SheetTitle>
        </SheetHeader>

        <nav className="flex-1 px-3 py-4">
          <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-sidebar-muted">{t('shell.workspace')}</p>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-150',
                  isActive
                    ? 'bg-accent/10 text-accent'
                    : 'text-sidebar-muted hover:bg-muted hover:text-white'
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}

          {/* Shared Vaults Section */}
          {(pendingCount > 0 || activeCount > 0) && (
            <div className="mt-5 border-t border-white/10 pt-4">
              <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-sidebar-muted">
                {t('nav.sharedWithMe')}
              </p>
              <NavLink
                to={ROUTES.SETTINGS}
                state={{ tab: 'shares' }}
                className={({ isActive }) =>
                  cn(
                    'flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-150',
                    isActive
                      ? 'bg-accent/10 text-accent'
                      : 'text-sidebar-muted hover:bg-muted hover:text-white'
                  )
                }
              >
                <div className="flex items-center gap-3">
                  <Users className="h-4 w-4" />
                  <span>{activeCount} {t('nav.sharedVaults')}</span>
                </div>
                {pendingCount > 0 && (
                  <Badge variant="highlight" className="ml-2">
                    {pendingCount}
                  </Badge>
                )}
              </NavLink>
            </div>
          )}
        </nav>

        <div className="mt-auto space-y-1 border-t border-white/10 p-4">
          <Button
            variant="ghost"
            className="w-full justify-start text-sidebar-muted hover:bg-muted hover:text-white"
            onClick={() => {
              setSidebarOpen(false);
              lock();
            }}
          >
            <Shield className="mr-2 h-4 w-4" />
            {t('auth.lock')}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-sidebar-muted hover:bg-red-500/10 hover:text-red-300"
            onClick={() => {
              setSidebarOpen(false);
              logout();
            }}
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t('auth.logout')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
